import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { UserRole } from '../../types/user';
import { authService } from '../../services/auth';
import { useAuth } from '../../contexts/AuthContext';
import { SEO } from '../../components/SEO';
import { GoogleSignInButton } from '../../components/auth/GoogleSignInButton';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';

const registerSchema = z.object({
  email: z.string().email(),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .refine((p) => /[A-Z]/.test(p), 'Add an uppercase letter')
    .refine((p) => /[0-9]/.test(p), 'Add a number')
    .refine((p) => /[^a-zA-Z0-9]/.test(p), 'Add a special character'),
  role: z.enum(['interviewer', 'interviewee'] as const),
});

type RegisterForm = z.infer<typeof registerSchema>;

const mapRoleToUserRole = (role: 'interviewer' | 'interviewee'): UserRole =>
  role === 'interviewer' ? UserRole.Interviewer : UserRole.Interviewee;

const extractFirstErrorMessage = (errors: unknown): string | undefined => {
  if (!errors) return undefined;
  if (typeof errors === 'string') return errors;
  if (Array.isArray(errors)) {
    for (const value of errors) {
      const nested = extractFirstErrorMessage(value);
      if (nested) return nested;
    }
    return undefined;
  }
  if (typeof errors === 'object') {
    for (const value of Object.values(errors as Record<string, unknown>)) {
      const nested = extractFirstErrorMessage(value);
      if (nested) return nested;
    }
  }
  return undefined;
};

const getTranslation = (t: (k: string) => string, key: string, fallback: string): string => {
  const translation = t(key);
  return translation === key ? fallback : translation;
};

const PASSWORD_RULES: Array<{
  key: 'length' | 'upper' | 'number' | 'special';
  label: string;
  test: (p: string) => boolean;
}> = [
  { key: 'length', label: '8+ chars', test: (p) => p.length >= 8 },
  { key: 'upper', label: 'A-Z', test: (p) => /[A-Z]/.test(p) },
  { key: 'number', label: '0-9', test: (p) => /[0-9]/.test(p) },
  { key: 'special', label: '!@#', test: (p) => /[^a-zA-Z0-9]/.test(p) },
];

function PasswordStrip({ value }: { value: string }) {
  return (
    <div className="mt-2 grid grid-cols-2 gap-1.5 sm:grid-cols-4">
      {PASSWORD_RULES.map((r) => {
        const ok = r.test(value);
        return (
          <div
            key={r.key}
            className={
              ok
                ? 'flex items-center justify-center gap-1 rounded-stitch-md bg-stitch-success-soft px-2 py-1 text-[11px] font-semibold text-stitch-success'
                : 'flex items-center justify-center gap-1 rounded-stitch-md bg-stitch-surface-2 px-2 py-1 text-[11px] font-semibold text-stitch-muted'
            }
          >
            <span className="material-symbols-outlined text-[14px]">
              {ok ? 'check_circle' : 'radio_button_unchecked'}
            </span>
            {r.label}
          </div>
        );
      })}
    </div>
  );
}

type RoleOption = {
  value: 'interviewee' | 'interviewer';
  title: string;
  blurb: string;
  icon: string;
};

export function Register() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Allow deep links (e.g. the "Become an Interviewer" page CTA) to preselect the role.
  const initialRole: RegisterForm['role'] =
    searchParams.get('role') === 'interviewer' ? 'interviewer' : 'interviewee';

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
  } = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
    mode: 'onChange',
    defaultValues: { role: initialRole },
  });

  const selectedRole = watch('role') ?? 'interviewee';
  const passwordValue = watch('password') ?? '';

  const roleOptions: RoleOption[] = [
    {
      value: 'interviewee',
      title: getTranslation(t, 'auth.intervieweeShort', "I'm practicing"),
      blurb: getTranslation(t, 'auth.intervieweeBlurb', 'Mock interviews, scoring, and coaching'),
      icon: 'school',
    },
    {
      value: 'interviewer',
      title: getTranslation(t, 'auth.interviewerShort', "I'm hiring or coaching"),
      blurb: getTranslation(t, 'auth.interviewerBlurb', 'Conduct interviews and share feedback'),
      icon: 'groups',
    },
  ];

  const onSubmit = async (data: RegisterForm) => {
    try {
      setIsLoading(true);
      setError(null);
      const userRole = mapRoleToUserRole(data.role);
      const response = await authService.register(data.email, data.password, userRole);
      login(response.token, response.userId, response.roleId);
      setTimeout(() => navigate('/profile/setup', { replace: true }), 0);
    } catch (err: unknown) {
      let message = getTranslation(t, 'auth.registrationFailed', 'Registration failed. Please try again.');

      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const data = err.response?.data;
        const serverMessage =
          (typeof data === 'string' ? data : undefined) ||
          data?.message ||
          data?.Message ||
          extractFirstErrorMessage(data?.errors);

        if (status === 409) {
          const normalizedMessage = (typeof serverMessage === 'string' ? serverMessage : '').toLowerCase();
          if (normalizedMessage.includes('username')) {
            message = getTranslation(t, 'auth.usernameAlreadyUsed', 'This username is already taken.');
          } else if (normalizedMessage.includes('email')) {
            message = getTranslation(t, 'auth.emailAlreadyUsed', 'An account with this email already exists. Try signing in.');
          } else {
            message = getTranslation(t, 'auth.accountConflict', 'An account with these details already exists.');
          }
        } else if (typeof serverMessage === 'string' && serverMessage.length > 0) {
          message = serverMessage;
        }
      } else if (err instanceof Error && err.message) {
        message = err.message;
      }

      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleCredential = async (credential: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await authService.googleLogin(credential);
      login(result.token, result.userId, result.roleId);
      const target = result.isNewUser ? '/profile/setup' : '/';
      setTimeout(() => navigate(target, { replace: true }), 0);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string; error?: string } }; message?: string };
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        getTranslation(t, 'auth.googleLoginFailed', 'Google login failed. Please try again.');
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Create account | InterviewR"
        description="Join InterviewR as a candidate or interviewer. Book mock interviews, practice with AI, and grow your career."
        url={typeof window !== 'undefined' ? window.location.href : 'https://interview-r.com/register'}
        noindex
      />
      <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-stitch-canvas px-4 py-12 sm:px-6 lg:px-8">
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            background:
              'radial-gradient(ellipse 80% 50% at 50% -20%, rgb(var(--stitch-primary) / 0.18), transparent), radial-gradient(ellipse 60% 40% at 100% 0%, rgb(var(--stitch-accent) / 0.16), transparent)',
          }}
        />

        <div className="relative z-[1] w-full max-w-lg">
          <div className="mb-6 flex items-center justify-between">
            <Link to="/" className="text-xs font-bold uppercase tracking-[0.3em] text-stitch-muted hover:text-stitch-fg">
              InterviewR
            </Link>
            <LanguageSwitcher />
          </div>

          <div className="mb-6 text-center">
            <h1 className="text-2xl font-bold tracking-tight text-stitch-fg sm:text-3xl">
              {getTranslation(t, 'auth.registerPageTitle', 'Create your account')}
            </h1>
            <p className="mt-2 text-sm text-stitch-muted">
              {getTranslation(t, 'auth.registerSubtitle', 'Practice interviews, book experts, and track progress.')}
            </p>
          </div>

          <div className="rounded-stitch-2xl border border-stitch-border bg-stitch-surface p-6 shadow-stitch-modal sm:p-8">
            <span className="mb-2 block text-xs font-semibold uppercase tracking-wider text-stitch-muted">
              {getTranslation(t, 'auth.iAm', 'I am')}
            </span>
            <div className="mb-5 grid grid-cols-2 gap-2">
              {roleOptions.map((opt) => {
                const active = selectedRole === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setValue('role', opt.value, { shouldValidate: true })}
                    aria-pressed={active}
                    className={
                      active
                        ? 'flex flex-col items-start gap-1 rounded-stitch-xl border-2 border-stitch-primary bg-stitch-primary-soft/60 p-3 text-left transition'
                        : 'flex flex-col items-start gap-1 rounded-stitch-xl border-2 border-stitch-border bg-stitch-surface p-3 text-left transition hover:border-stitch-muted'
                    }
                  >
                    <span
                      className={
                        active
                          ? 'flex h-7 w-7 items-center justify-center rounded-stitch-md bg-stitch-primary text-white'
                          : 'flex h-7 w-7 items-center justify-center rounded-stitch-md bg-stitch-surface-2 text-stitch-fg-soft'
                      }
                    >
                      <span className="material-symbols-outlined text-[18px]">{opt.icon}</span>
                    </span>
                    <span className={active ? 'text-sm font-bold text-stitch-primary' : 'text-sm font-bold text-stitch-fg'}>
                      {opt.title}
                    </span>
                    <span className="text-[11px] leading-tight text-stitch-muted">{opt.blurb}</span>
                  </button>
                );
              })}
            </div>
            {/* Hidden role input so RHF tracks the value via setValue. */}
            <input type="hidden" {...register('role')} value={selectedRole} readOnly />

            <div className="flex justify-center">
              <GoogleSignInButton
                text="signup_with"
                onCredential={handleGoogleCredential}
                onError={(e) =>
                  setError(
                    e.message || getTranslation(t, 'auth.googleLoginFailed', 'Google login failed. Please try again.'),
                  )
                }
              />
            </div>

            <div className="my-5 flex items-center">
              <div className="flex-grow border-t border-stitch-border" />
              <span className="mx-3 text-[10px] uppercase tracking-widest text-stitch-muted">
                {getTranslation(t, 'auth.orRegisterWithEmail', 'or with email')}
              </span>
              <div className="flex-grow border-t border-stitch-border" />
            </div>

            {error && (
              <div className="mb-4 rounded-stitch-xl border border-stitch-danger/40 bg-stitch-danger-soft px-4 py-3 text-stitch-danger" role="alert">
                <p className="text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <Input
                {...register('email')}
                type="email"
                autoComplete="email"
                label={getTranslation(t, 'auth.email', 'Email')}
                placeholder="you@example.com"
                error={
                  errors.email ? getTranslation(t, 'validation.email', 'Please enter a valid email address') : undefined
                }
              />

              <div>
                <Input
                  {...register('password')}
                  type="password"
                  autoComplete="new-password"
                  label={getTranslation(t, 'auth.password', 'Password')}
                  placeholder="••••••••"
                />
                <PasswordStrip value={passwordValue} />
              </div>

              <Button
                type="submit"
                disabled={isLoading || !isValid}
                variant="primary"
                size="lg"
                className="w-full"
              >
                {isLoading
                  ? getTranslation(t, 'common.loading', 'Creating your account...')
                  : getTranslation(t, 'auth.register', 'Create account')}
              </Button>
            </form>

            <p className="mt-5 text-center text-sm text-stitch-muted">
              {getTranslation(t, 'auth.hasAccount', 'Already have an account?')}{' '}
              <Link to="/login" className="font-semibold text-stitch-primary hover:text-stitch-primary-hover">
                {getTranslation(t, 'auth.login', 'Sign in')}
              </Link>
            </p>
          </div>

          <p className="mt-6 text-center text-xs text-stitch-muted">
            © {new Date().getFullYear()} InterviewR. {getTranslation(t, 'common.rights', 'All rights reserved.')}
          </p>
        </div>
      </div>
    </>
  );
}
